import { DateTimeTypeEnum } from "../../../types";

/**
 * Parses a year string with leading zeros (e.g., "0026" -> 2026)
 */
const parseYearWithLeadingZeros = (yearStr: string): number | null => {
  if (!yearStr.startsWith("0") || yearStr.length !== 4) return null;
  const firstNonZeroIndex = yearStr.search(/[1-9]/);
  if (firstNonZeroIndex === -1) return new Date().getFullYear();
  const nonZeroPortion = yearStr.substring(firstNonZeroIndex);
  const currentYear = new Date().getFullYear();
  const prefix = currentYear
    .toString()
    .substring(0, currentYear.toString().length - nonZeroPortion.length);
  const parsedYear = parseInt(prefix + nonZeroPortion);
  return parsedYear >= 1900 && parsedYear <= 9999 ? parsedYear : null;
};

/**
 * Parses a time string and returns hours and minutes
 * Extracts numbers and fills the rest with current time or defaults
 */
export const parseTimeString = (
  timeStr: string,
): { hours: number; minutes: number } | null => {
  const now = new Date();
  const numbers = timeStr.match(/\d+/g) || [];
  const hasAMPM = /(AM|PM)/i.test(timeStr);
  const isPM = /PM/i.test(timeStr);
  const hasColon = timeStr.includes(":");

  // Time with AM/PM
  if (hasAMPM && numbers.length >= 1) {
    let hours = parseInt(numbers[0]);
    const minutes = numbers.length === 2 ? parseInt(numbers[1]) : 0;
    if (isPM && hours !== 12) hours += 12;
    if (!isPM && hours === 12) hours = 0;
    if (hours >= 0 && hours <= 23 && minutes >= 0 && minutes <= 59) {
      return { hours, minutes };
    }
  }

  // Time without AM/PM
  if (hasColon && numbers.length === 2) {
    let hours = parseInt(numbers[0]);
    const minutes = parseInt(numbers[1]);
    // Default to current time period (AM/PM)
    if (hours >= 1 && hours <= 12) {
      const isPM = now.getHours() >= 12;
      if (isPM && hours !== 12) hours += 12;
      if (!isPM && hours === 12) hours = 0;
    }
    if (hours >= 0 && hours <= 23 && minutes >= 0 && minutes <= 59) {
      return { hours, minutes };
    }
  } else if (numbers.length === 1) {
    const num = parseInt(numbers[0]);
    // If just a number 0-59, treat as minutes
    if (num >= 0 && num <= 59) {
      return { hours: now.getHours(), minutes: num };
    }
  }
  return null;
};

/**
 * Parses a partial date string and returns month, day, and year
 * Extracts numbers and fills the rest with current date
 */
export const parsePartialDate = (
  dateStr: string,
): { month: number; day: number; year: number } | null => {
  const today = new Date();
  const numbers = dateStr.match(/\d+/g) || [];

  // Three numbers: MM/dd/yyyy
  if (numbers.length === 3) {
    const month = parseInt(numbers[0]);
    const day = parseInt(numbers[1]);
    // Check if year has leading zeros in original string
    const yearMatch = dateStr.match(/\/(\d{4})$/);
    let year = parseInt(numbers[2]);
    if (yearMatch && yearMatch[1].startsWith("0")) {
      const parsed = parseYearWithLeadingZeros(yearMatch[1]);
      if (parsed) year = parsed;
    }
    if (month >= 1 && month <= 12 && day >= 1 && day <= 31 && year >= 1900) {
      return { month: month - 1, day, year };
    }
  }

  // Two numbers: MM/dd
  if (numbers.length === 2) {
    const month = parseInt(numbers[0]);
    const day = parseInt(numbers[1]);
    if (month >= 1 && month <= 12 && day >= 1 && day <= 31) {
      return { month: month - 1, day, year: today.getFullYear() };
    }
  }

  // One number: determine position from string structure
  if (numbers.length === 1) {
    const num = parseInt(numbers[0]);
    const numIndex = dateStr.indexOf(numbers[0]);
    const beforeNum = dateStr.substring(0, numIndex);
    const slashesBefore = (beforeNum.match(/\//g) || []).length;

    // Check for placeholders to determine position
    const hasMonthPlaceholder = /MM|M{2,}/i.test(beforeNum);
    const hasDayPlaceholder = /dd|d{2,}/i.test(beforeNum);

    if (hasDayPlaceholder || slashesBefore >= 2) {
      // Year position (after day placeholder or after 2 slashes)
      // Check if year has leading zeros in original string (look for 4-digit pattern)
      const yearMatch = dateStr.match(/(\d{4})$/);
      let year = num;
      if (
        yearMatch &&
        yearMatch[1].startsWith("0") &&
        yearMatch[1].length === 4
      ) {
        const parsed = parseYearWithLeadingZeros(yearMatch[1]);
        if (parsed) year = parsed;
      }
      return {
        month: today.getMonth(),
        day: today.getDate(),
        year,
      };
    } else if (hasMonthPlaceholder || slashesBefore === 1) {
      // Day position (after month placeholder or after 1 slash)
      return {
        month: today.getMonth(),
        day: num,
        year: today.getFullYear(),
      };
    } else {
      // Month position (at start, no slashes before)
      return {
        month: num - 1,
        day: today.getDate(),
        year: today.getFullYear(),
      };
    }
  }

  return null;
};

/**
 * Creates a Date from date and time parts, using current date/time for missing parts
 */
const createDateFromParts = (
  dateParts: { month: number; day: number; year: number } | null,
  timeParts: { hours: number; minutes: number } | null,
): Date => {
  const now = new Date();
  const date = dateParts
    ? new Date(dateParts.year, dateParts.month, dateParts.day)
    : new Date(now);
  const time = timeParts || {
    hours: now.getHours(),
    minutes: now.getMinutes(),
  };
  date.setHours(time.hours, time.minutes, 0, 0);
  return date;
};

/**
 * Parses input field value based on the date/time type
 */
export const parseInputFieldValue = (
  inputValue: string,
  type: DateTimeTypeEnum,
): { value: Date | null; isValid: boolean } => {
  try {
    if (type === DateTimeTypeEnum.TIME) {
      const timeParts = parseTimeString(inputValue);
      if (timeParts) {
        return { value: createDateFromParts(null, timeParts), isValid: true };
      }
    } else if (type === DateTimeTypeEnum.DATE_TIME) {
      const parts = inputValue.split(" ");
      const datePart = parts[0] || "";
      const timePart = parts.slice(1).join(" ") || parts[0];

      const dateParts = parsePartialDate(datePart);
      const timeParts = parseTimeString(timePart);

      if (dateParts || timeParts) {
        return {
          value: createDateFromParts(dateParts, timeParts),
          isValid: true,
        };
      }
    } else if (type === DateTimeTypeEnum.DATE) {
      const dateParts = parsePartialDate(inputValue);
      if (dateParts) {
        return { value: createDateFromParts(dateParts, null), isValid: true };
      }
    }
  } catch (e) {
    // Parsing failed
  }
  return { value: null, isValid: false };
};

/**
 * Determines which value to save (input field or calendar selection)
 * Simple priority: input value over calendar value
 */
export const selectValueToSave = (
  inputValue: Date | null,
  calendarValue: Date | null,
): Date | null => {
  if (inputValue) return inputValue;
  if (calendarValue) return calendarValue;
  return null;
};

/**
 * Normalizes a date based on the date/time type
 */
export const normalizeDateByType = (
  date: Date,
  type: DateTimeTypeEnum,
): Date => {
  const today = new Date();
  const now = new Date();

  if (type === DateTimeTypeEnum.TIME) {
    date.setFullYear(today.getFullYear(), today.getMonth(), today.getDate());
  } else if (type === DateTimeTypeEnum.DATE_TIME) {
    if (isNaN(date.getTime()) || date.getFullYear() < 1900) {
      date.setFullYear(today.getFullYear(), today.getMonth(), today.getDate());
      date.setHours(now.getHours(), now.getMinutes(), 0, 0);
    }
  }
  return date;
};
